import React, { memo } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { Camera, Sun, Mountain, Smartphone } from 'lucide-react';
const tips = [{
  icon: Camera,
  title: 'Camera Settings for Mountains',
  points: ['Use aperture priority mode (f/8-f/11) for landscape depth', 'ISO 100-400 in daylight, up to 1600 in low light', 'Shoot in RAW format for maximum editing flexibility', 'Use exposure compensation (+0.5 to +1) for snow scenes']
}, {
  icon: Sun,
  title: 'Golden Hour Magic',
  points: ['Shoot during sunrise and sunset for warm, dramatic light', 'Blue hour (before sunrise/after sunset) creates ethereal moods', 'Midday harsh light works for silhouettes and stark contrasts', 'Overcast days provide soft, even lighting for portraits']
}, {
  icon: Mountain,
  title: 'Composition Techniques',
  points: ['Use foreground elements (rocks, prayer flags) for depth', 'Rule of thirds: place horizons on upper or lower third', 'Include people for scale in mountain landscapes', "Leading lines (trails, rivers) guide viewer's eye"]
}, {
  icon: Smartphone,
  title: 'Protecting Your Gear',
  points: ['Keep batteries warm in inside pockets (cold drains them fast)', 'Use lens hoods to prevent flare and protect from bumps', 'Bring extra memory cards and batteries', 'Silica gel packets prevent moisture damage']
}];
export function PhotographyTipsPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-1763">
      <Navbar data-id="element-1764" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-1765">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2070&auto=format&fit=crop")'
      }} data-id="element-1766">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-purple-900/50 to-purple-900/80" data-id="element-1767" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-1768">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1769">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1770">
              <Camera className="text-white" size={32} data-id="element-1771" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-1772">
            Photography Tips for Himalayan Treks
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-purple-100 font-light max-w-2xl mx-auto" data-id="element-1773">
            Capture the majesty of the Himalayas with professional techniques
          </motion.p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1774">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1775">
          <SectionHeading title="Photographing the Himalayas" subtitle="Overview" data-id="element-1776" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-1777">
            <p data-id="element-1778">
              The Himalayas offer some of the most spectacular photography
              opportunities on Earth. From towering snow-capped peaks to
              colorful prayer flags, ancient monasteries to warm Sherpa smiles,
              every moment is worth capturing.
            </p>
            <p data-id="element-1779">
              However, high-altitude photography presents unique challenges:
              extreme cold affects batteries, intense sunlight creates harsh
              contrasts, and the thin air makes carrying heavy gear exhausting.
              This guide will help you overcome these challenges and return home
              with stunning images.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Tips Grid */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-1780">
        <div className="max-w-7xl mx-auto" data-id="element-1781">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1782">
            {tips.map((tip, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1783">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6" data-id="element-1784">
                  <tip.icon size={28} className="text-white" data-id="element-1785" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-4" data-id="element-1786">
                  {tip.title}
                </h3>
                <ul className="space-y-3" data-id="element-1787">
                  {tip.points.map((point, pointIndex) => <li key={pointIndex} className="flex items-start gap-3" data-id="element-1788">
                      <span className="text-purple-500 font-bold" data-id="element-1789">•</span>
                      <span className="text-stone-700" data-id="element-1790">{point}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gear Recommendations */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1791">
        <SectionHeading title="Recommended Gear" subtitle="What to bring" data-id="element-1792" />
        <div className="space-y-6" data-id="element-1793">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6" data-id="element-1794">
            <h3 className="text-lg font-bold text-stone-900 mb-3" data-id="element-1795">
              Camera Body
            </h3>
            <p className="text-stone-700 leading-relaxed" data-id="element-1796">
              Mirrorless or DSLR with good low-light performance. Weather-sealed
              bodies are ideal but not essential. Smartphone cameras are
              surprisingly capable for social media and casual shots.
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6" data-id="element-1797">
            <h3 className="text-lg font-bold text-stone-900 mb-3" data-id="element-1798">Lenses</h3>
            <p className="text-stone-700 leading-relaxed" data-id="element-1799">
              Wide-angle (16-35mm) for landscapes, standard zoom (24-70mm) for
              versatility, telephoto (70-200mm) for distant peaks and wildlife.
              If carrying one lens, choose 24-70mm equivalent.
            </p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6" data-id="element-1800">
            <h3 className="text-lg font-bold text-stone-900 mb-3" data-id="element-1801">
              Accessories
            </h3>
            <p className="text-stone-700 leading-relaxed" data-id="element-1802">
              Lightweight tripod, polarizing filter (reduces glare, enhances
              skies), ND filters for long exposures, lens cleaning kit, spare
              batteries (at least 3), large capacity memory cards.
            </p>
          </div>
        </div>
      </section>

      {/* Best Shots */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-1803">
        <div className="max-w-7xl mx-auto" data-id="element-1804">
          <SectionHeading title="Must-Capture Moments" subtitle="Don't miss these shots" data-id="element-1805" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1806">
            {[{
            title: 'Sunrise on Everest',
            desc: 'From Kala Patthar or Gokyo Ri'
          }, {
            title: 'Prayer Flags',
            desc: 'Colorful flags against blue sky'
          }, {
            title: 'Sherpa Portraits',
            desc: 'Warm, genuine smiles'
          }, {
            title: 'Mountain Reflections',
            desc: 'In glacial lakes'
          }, {
            title: 'Monasteries',
            desc: 'Tengboche, Pangboche'
          }, {
            title: 'Yak Caravans',
            desc: 'Traditional transport'
          }].map((shot, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 text-center" data-id="element-1807">
                <h3 className="text-lg font-bold text-stone-900 mb-2" data-id="element-1808">
                  {shot.title}
                </h3>
                <p className="text-stone-600 text-sm" data-id="element-1809">{shot.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-id="element-1810">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1811">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1812">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1813">
              Ready to Capture Your Adventure?
            </h2>
            <p className="text-xl text-purple-100 mb-8" data-id="element-1814">
              Join us for a trek designed with photography in mind
            </p>
            <a href="#plan-trip" data-id="element-1815">
              <button className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-purple-50 transition-colors" data-id="element-1816">
                Plan Your Trek
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1817" />
    </div>;
}